import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { USER_ROLES_SLICE_NAME } from './constants';
import {
	TAssignUserRolesRequest,
	TGetUserRolesGroupedRequest,
	TGetUserRolesGroupedResponsePayload,
	TGetUserRolesByUserIdRequest,
	TGetUserRolesByUserIdResponsePayload,
	TGetUserRolesRequest,
	TGetUserRolesResponsePayload,
	TGetRolesResponsePayload,
	TRemoveUserRoleRequest,
	TUpdateUserRoleRequest,
	TUserRolesStore,
} from './types';

const initialState: TUserRolesStore = {
	roles: null,
	rolesLoading: false,
	rolesFailure: false,

	grouped: null,
	groupedLoading: false,
	groupedFailure: false,

	userRoles: null,
	userRolesLoading: false,
	userRolesFailure: false,

	userRolesByUserId: null,
	userRolesByUserIdLoading: false,
	userRolesByUserIdFailure: false,

	assignLoading: false,
	assignSuccess: false,
	updateLoading: false,
	updateSuccess: false,
	removeLoading: false,
	removeSuccess: false,
};

const userRoles = createSlice({
	name: USER_ROLES_SLICE_NAME,
	initialState,
	reducers: {
		getRolesRequest: (state: TUserRolesStore) => {
			const s = state;
			s.rolesLoading = true;
			s.rolesFailure = false;
		},
		getRolesSuccess: (state: TUserRolesStore, action: PayloadAction<TGetRolesResponsePayload>) => {
			const s = state;
			s.roles = action.payload;
			s.rolesLoading = false;
			s.rolesFailure = false;
		},
		getRolesFailure: (state: TUserRolesStore) => {
			const s = state;
			s.roles = null;
			s.rolesLoading = false;
			s.rolesFailure = true;
		},

		getUserRolesGroupedRequest: (
			state: TUserRolesStore,
			action: PayloadAction<TGetUserRolesGroupedRequest>,
		) => {
			const s = state;
			s.groupedLoading = true;
			s.groupedFailure = false;
		},
		getUserRolesGroupedSuccess: (
			state: TUserRolesStore,
			action: PayloadAction<TGetUserRolesGroupedResponsePayload>,
		) => {
			const s = state;
			s.grouped = action.payload;
			s.groupedLoading = false;
			s.groupedFailure = false;
		},
		getUserRolesGroupedFailure: (state: TUserRolesStore) => {
			const s = state;
			s.grouped = null;
			s.groupedLoading = false;
			s.groupedFailure = true;
		},

		getUserRolesRequest: (state: TUserRolesStore, action: PayloadAction<TGetUserRolesRequest>) => {
			const s = state;
			s.userRolesLoading = true;
			s.userRolesFailure = false;
		},
		getUserRolesSuccess: (
			state: TUserRolesStore,
			action: PayloadAction<TGetUserRolesResponsePayload>,
		) => {
			const s = state;
			s.userRoles = action.payload;
			s.userRolesLoading = false;
			s.userRolesFailure = false;
		},
		getUserRolesFailure: (state: TUserRolesStore) => {
			const s = state;
			s.userRoles = null;
			s.userRolesLoading = false;
			s.userRolesFailure = true;
		},

		getUserRolesByUserIdRequest: (
			state: TUserRolesStore,
			action: PayloadAction<TGetUserRolesByUserIdRequest>,
		) => {
			const s = state;
			s.userRolesByUserIdLoading = true;
			s.userRolesByUserIdFailure = false;
		},
		getUserRolesByUserIdSuccess: (
			state: TUserRolesStore,
			action: PayloadAction<TGetUserRolesByUserIdResponsePayload>,
		) => {
			const s = state;
			s.userRolesByUserId = action.payload;
			s.userRolesByUserIdLoading = false;
			s.userRolesByUserIdFailure = false;
		},
		getUserRolesByUserIdFailure: (state: TUserRolesStore) => {
			const s = state;
			s.userRolesByUserId = null;
			s.userRolesByUserIdLoading = false;
			s.userRolesByUserIdFailure = true;
		},

		assignUserRolesRequest: (
			state: TUserRolesStore,
			action: PayloadAction<TAssignUserRolesRequest>,
		) => {
			const s = state;
			s.assignLoading = true;
			s.assignSuccess = false;
		},
		assignUserRolesSuccess: (state: TUserRolesStore) => {
			const s = state;
			s.assignLoading = false;
			s.assignSuccess = true;
		},
		assignUserRolesFailure: (state: TUserRolesStore) => {
			const s = state;
			s.assignLoading = false;
			s.assignSuccess = false;
		},

		updateUserRoleRequest: (
			state: TUserRolesStore,
			action: PayloadAction<TUpdateUserRoleRequest>,
		) => {
			const s = state;
			s.updateLoading = true;
			s.updateSuccess = false;
		},
		updateUserRoleSuccess: (state: TUserRolesStore) => {
			const s = state;
			s.updateLoading = false;
			s.updateSuccess = true;
		},
		updateUserRoleFailure: (state: TUserRolesStore) => {
			const s = state;
			s.updateLoading = false;
			s.updateSuccess = false;
		},

		removeUserRoleRequest: (
			state: TUserRolesStore,
			action: PayloadAction<TRemoveUserRoleRequest>,
		) => {
			const s = state;
			s.removeLoading = true;
			s.removeSuccess = false;
		},
		removeUserRoleSuccess: (state: TUserRolesStore) => {
			const s = state;
			s.removeLoading = false;
			s.removeSuccess = true;
		},
		removeUserRoleFailure: (state: TUserRolesStore) => {
			const s = state;
			s.removeLoading = false;
			s.removeSuccess = false;
		},
	},
});

export default userRoles.reducer;

export const {
	getRolesRequest,
	getRolesSuccess,
	getRolesFailure,
	getUserRolesGroupedRequest,
	getUserRolesGroupedSuccess,
	getUserRolesGroupedFailure,
	getUserRolesRequest,
	getUserRolesSuccess,
	getUserRolesFailure,
	getUserRolesByUserIdRequest,
	getUserRolesByUserIdSuccess,
	getUserRolesByUserIdFailure,
	assignUserRolesRequest,
	assignUserRolesSuccess,
	assignUserRolesFailure,
	updateUserRoleRequest,
	updateUserRoleSuccess,
	updateUserRoleFailure,
	removeUserRoleRequest,
	removeUserRoleSuccess,
	removeUserRoleFailure,
} = userRoles.actions;
